import { useState, useRef, useEffect } from "react";
import { useComparador } from "@/hooks/useComparador";
import { FilterPanel } from "@/components/FilterPanel";
import { CameraCard } from "@/components/CameraCard";
import { Camera } from "@/data/cameras";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowLeftRight, Camera as CameraIcon, Menu, X, ChevronRight, Grid3X3 } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import type { Lang } from "@/i18n/translations";

// Flag emoji components
const FLAGS: Record<Lang, { emoji: string; label: string }> = {
  pt: { emoji: "🇵🇹", label: "PT" },
  es: { emoji: "🇪🇸", label: "ES" },
};

function LangSelector() {
  const { lang, setLang } = useLang();
  return (
    <div className="hidden sm:flex items-center gap-1 shrink-0">
      {(Object.entries(FLAGS) as [Lang, { emoji: string; label: string }][]).map(([code, { emoji, label }]) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          title={label}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all ${
            lang === code
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          <span className="text-base leading-none">{emoji}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

export default function Home() {
  const {
    filters, updateFilter, resetFilters,
    sortKey, setSortKey,
    filteredCameras,
    modelInput, setModelInput,
    modelSuggestions,
    equivalentResults, searchedCamera, selectedModel, noEquivalentFound,
    searchEquivalent, selectModelFromSuggestion, resetEquivalentSearch,
    totalCameras,
  } = useComparador();

  const { t } = useLang();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"catalog" | "compare">("catalog");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleModelInputChange = (v: string) => {
    setModelInput(v);
    setShowSuggestions(v.length >= 2);
  };

  const handleSelectSuggestion = (cam: Camera) => {
    selectModelFromSuggestion(cam);
    setShowSuggestions(false);
  };

  const handleSearch = () => {
    setShowSuggestions(false);
    if (selectedModel) {
      searchEquivalent(selectedModel);
    } else if (modelInput.length >= 2) {
      const first = modelSuggestions[0];
      if (first) {
        selectModelFromSuggestion(first);
        searchEquivalent(first);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
    if (e.key === "Escape") setShowSuggestions(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 h-14 flex items-center gap-3">
          {/* Logo Contera */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <CameraIcon className="w-4 h-4 text-primary-foreground" />
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-bold text-foreground leading-none">{t("appName")}</div>
              <div className="text-xs text-muted-foreground">{t("appSubtitle")}</div>
            </div>
          </div>

          {/* Tabs — visíveis e proeminentes */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex rounded-xl border border-border overflow-hidden bg-muted">
              <button
                onClick={() => setActiveTab("catalog")}
                className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 text-sm font-semibold transition-all ${
                  activeTab === "catalog"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
                <span>{t("tabCatalog")}</span>
                {activeTab === "catalog" && (
                  <span className="hidden sm:inline text-xs bg-primary-foreground/20 px-1.5 py-0.5 rounded-full ml-1">
                    {filteredCameras.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("compare")}
                className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 text-sm font-semibold transition-all ${
                  activeTab === "compare"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                <ArrowLeftRight className="w-4 h-4" />
                <span>{t("tabCompare")}</span>
              </button>
            </div>
          </div>

          {/* Search bar (catalog) */}
          {activeTab === "catalog" && (
            <div className="relative hidden sm:flex items-center w-56">
              <Search className="absolute left-3 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={filters.searchQuery}
                onChange={e => updateFilter("searchQuery", e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="pl-9 h-8 text-xs bg-background"
              />
            </div>
          )}

          {/* Language selector */}
          <LangSelector />

          {/* Mobile menu toggle */}
          <button
            className="sm:hidden p-2 rounded-md hover:bg-muted shrink-0"
            onClick={() => setSidebarOpen(v => !v)}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {activeTab === "catalog" ? (
        <div className="flex flex-1 overflow-hidden max-w-screen-2xl mx-auto w-full">
          {/* Sidebar */}
          <div className={`
            fixed inset-y-0 left-0 z-20 w-72 top-14 sm:relative sm:block sm:w-64 lg:w-72 shrink-0
            transition-transform duration-200
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full sm:translate-x-0"}
          `}>
            <FilterPanel
              filters={filters}
              updateFilter={updateFilter}
              resetFilters={resetFilters}
              sortKey={sortKey}
              setSortKey={setSortKey}
              totalShowing={filteredCameras.length}
              totalAll={totalCameras}
            />
          </div>

          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 top-14 bg-black/40 z-10 sm:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Main grid */}
          <main className="flex-1 overflow-y-auto p-4">
            {/* Mobile search */}
            <div className="sm:hidden mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={filters.searchQuery}
                onChange={e => updateFilter("searchQuery", e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="pl-9 h-9 text-sm bg-background"
              />
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{filteredCameras.length}</span> {t("cameras")}
                {filters.brand !== "Todas" && <span className="ml-1 text-primary font-medium">{filters.brand}</span>}
              </p>
              {filteredCameras.length > 0 && (
                <div className="flex gap-1">
                  {filteredCameras.filter(c=>c.brand==="DAHUA").length > 0 && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      {filteredCameras.filter(c=>c.brand==="DAHUA").length} DAHUA
                    </span>
                  )}
                  {filteredCameras.filter(c=>c.brand==="DAHUA").length > 0 && filteredCameras.filter(c=>c.brand==="TVT").length > 0 && (
                    <span className="text-xs text-muted-foreground">·</span>
                  )}
                  {filteredCameras.filter(c=>c.brand==="TVT").length > 0 && (
                    <span className="text-xs text-orange-600 dark:text-orange-400">
                      {filteredCameras.filter(c=>c.brand==="TVT").length} TVT
                    </span>
                  )}
                </div>
              )}
            </div>

            {filteredCameras.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <CameraIcon className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground font-medium">{t("noResults")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("noResultsHint")}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={resetFilters}>
                  {t("clearFilters")}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {filteredCameras.map(cam => (
                  <CameraCard key={cam.id} camera={cam} />
                ))}
              </div>
            )}
          </main>
        </div>
      ) : (
        /* Compare tab */
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-4 sm:p-8">
            {/* Hero */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-2xl px-5 py-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center text-sm font-bold text-blue-700 dark:text-blue-300">D</div>
                <ArrowLeftRight className="w-5 h-5 text-primary" />
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/40 rounded-full flex items-center justify-center text-sm font-bold text-orange-700 dark:text-orange-300">T</div>
              </div>
              <h2 className="text-2xl font-bold text-foreground">{t("compareTitle")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("compareSubtitle")}</p>
            </div>

            {/* Smart Search Box */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm mb-6">
              <label className="text-sm font-semibold text-foreground block mb-1">
                {t("modelToSearch")}
              </label>
              <p className="text-xs text-muted-foreground mb-3">{t("modelHint")}</p>

              <div className="relative" ref={suggestionsRef}>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      ref={inputRef}
                      value={modelInput}
                      onChange={e => handleModelInputChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => modelInput.length >= 2 && setShowSuggestions(true)}
                      placeholder={t("comparePlaceholder")}
                      className="pl-10 font-mono text-sm"
                      autoComplete="off"
                    />
                    {modelInput && (
                      <button
                        onClick={resetEquivalentSearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={!selectedModel && modelInput.length < 2}
                    className="shrink-0"
                  >
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    {t("compareBtn")}
                  </Button>
                </div>

                {/* Suggestions dropdown */}
                {showSuggestions && modelSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/50 border-b border-border">
                      {modelSuggestions.length} {modelSuggestions.length !== 1 ? t("resultsPlural") : t("results")}
                    </div>
                    {modelSuggestions.map(cam => (
                      <button
                        key={cam.id}
                        onClick={() => handleSelectSuggestion(cam)}
                        className="w-full text-left px-4 py-2.5 hover:bg-accent flex items-center gap-3 border-b border-border/50 last:border-0 transition-colors"
                      >
                        <span
                          className={`text-xs font-bold px-1.5 py-0.5 rounded shrink-0 ${
                            cam.brand === "DAHUA"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                              : "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                          }`}
                        >
                          {cam.brand}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono font-semibold text-foreground truncate">{cam.model}</p>
                          <p className="text-xs text-muted-foreground truncate">{cam.megapixels}MP · {cam.type} · {cam.lensType}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          {cam.price && <p className="text-xs font-semibold text-foreground">{cam.price}</p>}
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected model chip */}
              {selectedModel && !searchedCamera && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t("selected")}</span>
                  <Badge variant="secondary" className="font-mono text-xs gap-1">
                    <span className={selectedModel.brand === "DAHUA" ? "text-blue-600 dark:text-blue-400" : "text-orange-600 dark:text-orange-400"}>
                      {selectedModel.brand}
                    </span>
                    {selectedModel.model}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{t("clickToCompare")}</span>
                </div>
              )}
            </div>

            {/* Results */}
            {searchedCamera && (
              <div className="space-y-5">
                {/* Source camera */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${searchedCamera.brand === "DAHUA" ? "bg-blue-500" : "bg-orange-500"}`} />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("selectedCamera")} ({searchedCamera.brand})
                    </h3>
                  </div>
                  <CameraCard camera={searchedCamera} highlight />
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-dashed border-border" />
                  <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
                    <ArrowLeftRight className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-primary">{t("equivalent")}</span>
                  </div>
                  <div className="flex-1 border-t border-dashed border-border" />
                </div>

                {/* Equivalents */}
                {equivalentResults.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full ${equivalentResults[0].brand === "DAHUA" ? "bg-blue-500" : "bg-orange-500"}`} />
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {equivalentResults[0].brand === searchedCamera?.brand ? t("similarModels") : `${t("equivalent")} ${equivalentResults[0].brand}`}
                      </h3>
                    </div>
                    <div className="grid gap-3">
                      {equivalentResults.map(cam => (
                        <CameraCard key={cam.id} camera={cam} highlight />
                      ))}
                    </div>
                  </div>
                ) : noEquivalentFound ? (
                  <div className="bg-muted/50 border border-border rounded-xl p-6 text-center">
                    <p className="text-sm text-muted-foreground">{t("noEquivalent")}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t("contactCommercial")}</p>
                    <a
                      href="mailto:comercial@contera.pt"
                      className="mt-3 inline-block text-xs text-primary hover:underline"
                    >
                      comercial@contera.pt
                    </a>
                  </div>
                ) : null}
              </div>
            )}

            {/* Initial state — info cards */}
            {!searchedCamera && !noEquivalentFound && (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-card border border-border rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">183</div>
                    <div className="text-xs text-muted-foreground mt-1">{t("dahuaCameras")}</div>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">72</div>
                    <div className="text-xs text-muted-foreground mt-1">{t("tvtCameras")}</div>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-primary">255</div>
                    <div className="text-xs text-muted-foreground mt-1">{t("totalCatalog")}</div>
                  </div>
                </div>

                {/* How to use */}
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{t("howToUse")}</h3>
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>{t("step1")}</li>
                    <li>{t("step2")}</li>
                    <li>{t("step3")} <strong className="text-foreground">"{t("compareBtn")}"</strong> {t("step3b")}</li>
                  </ol>
                </div>

                {/* Quick tip */}
                <div className="text-xs text-muted-foreground text-center">
                  {t("catalogTip")} <button onClick={() => setActiveTab("catalog")} className="text-primary hover:underline font-medium">{t("catalogTipLink")}</button> {t("catalogTipEnd")}
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      {/* Footer */}
      <footer className="border-t border-border bg-card py-3 text-center text-xs text-muted-foreground shrink-0">
        {t("footer")}
        <span className="mx-2">·</span>
        {t("footerPrices")} <a href="mailto:comercial@contera.pt" className="hover:text-foreground">comercial@contera.pt</a>
      </footer>
    </div>
  );
}
